import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const HeroSection = () => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Particle / grid animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let w = 0;
    let h = 0;

    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Particles
    const PARTICLE_COUNT = 80;
    type Particle = {
      x: number; y: number; vx: number; vy: number; r: number; alpha: number;
    };
    const particles: Particle[] = Array.from({ length: PARTICLE_COUNT }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.5 + 0.5,
      alpha: Math.random() * 0.5 + 0.2,
    }));

    // Grid lines
    const GRID_COLS = 20;
    const GRID_ROWS = 12;

    let t2 = 0;

    const draw = () => {
      ctx.clearRect(0, 0, w, h);

      // Animated grid
      const cellW = w / GRID_COLS;
      const cellH = h / GRID_ROWS;
      ctx.strokeStyle = "rgba(6,182,212,0.07)";
      ctx.lineWidth = 0.5;
      for (let i = 0; i <= GRID_COLS; i++) {
        const x = i * cellW;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let j = 0; j <= GRID_ROWS; j++) {
        const y = j * cellH;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      // Glowing scan line
      const scanY = ((Math.sin(t2 * 0.5) + 1) / 2) * h;
      const grad = ctx.createLinearGradient(0, scanY - 60, 0, scanY + 60);
      grad.addColorStop(0, "rgba(6,182,212,0)");
      grad.addColorStop(0.5, "rgba(6,182,212,0.08)");
      grad.addColorStop(1, "rgba(6,182,212,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, scanY - 60, w, 120);

      // Particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(6,182,212,${p.alpha})`;
        ctx.fill();
      });

      // Connect nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(6,182,212,${0.12 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      t2 += 0.01;
      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#020b18]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=ultra-modern-futuristic-digital-financial-technology-abstract-dark-background-with-glowing-cyan-blue-circuit-patterns-data-streams-holographic-grid-lines-deep-space-dark-navy-atmosphere-no-people-clean-minimal-global-fintech-aesthetic&width=1920&height=1080&seq=hero001&orientation=landscape"
          alt="hero background"
          className="w-full h-full object-cover object-center opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#020b18] via-[#020b18]/80 to-[#020b18]/60" />
      </div>

      {/* Canvas overlay */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />

      {/* Glowing orbs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full bg-cyan-500/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/3 w-64 h-64 rounded-full bg-cyan-400/8 blur-3xl pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-16">
        <div className="max-w-4xl">
          {/* Label */}
          <div
            className={`inline-flex items-center gap-2 mb-8 transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
            style={{ transitionDelay: "0.1s" }}
          >
            <span className="w-6 h-px bg-cyan-400" />
            <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">
              Stream Solutions Limited
            </span>
          </div>

          {/* Main headline */}
          <h1
            className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6 transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0.2s" }}
          >
            <span className="block">{t("hero.catchcopy").split("、")[0]}、</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-cyan-200">
              {t("hero.catchcopy").split("、").slice(1).join("、")}
            </span>
          </h1>

          {/* Sub */}
          <p
            className={`text-slate-300 text-base lg:text-lg leading-relaxed max-w-2xl mb-10 transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0.35s" }}
          >
            {t("hero.sub")}
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "0.5s" }}
          >
            <Link
              to="/service"
              className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              {t("hero.cta")}
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" />
              </span>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border border-white/30 hover:border-cyan-400 text-white hover:text-cyan-300 font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {t("hero.cta2")}
            </Link>
          </div>
        </div>

        {/* Stats bar */}
        <div
          className={`mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "0.65s" }}
        >
          {[
            { value: "150+", label: t("top.stats.clients") },
            { value: "99.8%", label: t("top.stats.accuracy") },
            { value: "24/7", label: t("top.stats.support") },
            { value: "10+", label: t("top.stats.years") },
          ].map((stat) => (
            <div
              key={stat.label}
              className="border border-white/10 rounded-2xl p-5 bg-white/5 backdrop-blur-sm"
            >
              <div className="text-2xl lg:text-3xl font-bold text-cyan-400 mb-1">
                {stat.value}
              </div>
              <div className="text-xs text-slate-400 tracking-wide">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-slate-500 text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-8 bg-gradient-to-b from-slate-500 to-transparent" />
      </div>
    </section>
  );
};

export default HeroSection;
